import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { getIndoorBeacons } from "../../../../redux/actions";
import { useNavigate, useParams } from "react-router-dom";
import IndoorBeaconEditor from "../../components/creatingBeacon/IndoorBeaconEditor";
import { IndoorBeacon } from "../../../../types/models/IndoorBeacon";
import EditIcon from '@mui/icons-material/Edit';


interface IndoorBeaconListProps {
  refreshFlag: boolean;
  setRefreshFlag: (flag: boolean) => void;
  openIndoorBeaconEditor: boolean;
  setOpenIndoorBeaconEditor: (flag: boolean) => void;
}

const IndoorBeaconList: React.FC<IndoorBeaconListProps> = ({ refreshFlag, setRefreshFlag, setOpenIndoorBeaconEditor, openIndoorBeaconEditor }) => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const businessGroupState = useAppSelector(state => state.businessGroup);
  const beaconState = useAppSelector(state => state.beacon);
  const { group_id } = useParams<{ group_id: string }>();

  const [currentPage, setCurrentPage] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedBeacon, setSelectedBeacon] = useState<IndoorBeacon | null>(null);

  useEffect(() => {
    if (currentPage !== undefined) {
      getBusinessBeacons();

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (businessGroupState.currentIndoorExperience !== undefined) {
      callGettingBeaconsForFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessGroupState.currentIndoorExperience]);

   useEffect(() => {
      if (refreshFlag === true) {
        callGettingBeaconsForFirstPage();
        setRefreshFlag(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshFlag]);
  
    const callGettingBeaconsForFirstPage = () => {
      if (currentPage === 0) {
        getBusinessBeacons();
      }
      else {
        setCurrentPage(0);
      }
    };
  
    const getBusinessBeacons = () => {
      setIsLoading(true);
        dispatch(getIndoorBeacons(
          businessGroupState.currentIndoorExperience?.id ?? null,
          currentPage ?? 0,
          () => { 
            setIsLoading(false);
          })
        );
    };

    const handleRowClick = (params: any) => {
      setSelectedBeacon(params.row);
      setOpenIndoorBeaconEditor(true);
      navigate(`/beacons/indoor/${group_id}/editor/${params.row.id}`);
    };

    const handleSubmitted = () => {
    // Reload data
      setOpenIndoorBeaconEditor(false);
      setRefreshFlag(true);
      navigate(`/beacons/indoor/${group_id}`);
    };

    const columns = useMemo<GridColDef<IndoorBeacon>[]>( () => [
      {
        field: 'name', 
        headerName: 'Name', 
        flex: 1,
        renderCell: (params: any) => {

          return (
            <Box sx={{ display: "flex", flexDirection: "column", overflow: "hidden", maxWidth: "600px" }}>
              <Box
                sx={{
                  fontWeight: 500,
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {params.row.name}
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  lineHeight: 1.4,
                }}
              >
                {params.row.description}
              </Box>
            </Box>
          );
        }
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 70,
        align: 'center',
        headerAlign: 'center',
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleRowClick(params)}
          />,
        ]
      }
    ],
    [businessGroupState.currentIndoorExperience, beaconState.currentIndoorBeacons],
    );

    return (
        <Box sx={{ width: '100%' }}>
          {openIndoorBeaconEditor ? (
            <IndoorBeaconEditor
              beacon={selectedBeacon}
              onSubmitted={handleSubmitted}
              onClose={() => {
                setOpenIndoorBeaconEditor(false);
                navigate(`/beacons/indoor/${group_id}`);
              }}
            />
          ) : (
          <Paper sx={{ backgroundColor: "white" }} elevation={1}>
            <DataGrid
            columns={columns}
            rows={beaconState.currentIndoorBeacons}
            getRowId={row=>row.id}
            onRowClick={handleRowClick}
            rowHeight={80}
            loading={isLoading}
            slots={{
                toolbar: GridToolbar,
            }}
            slotProps={{
                loadingOverlay: {
                variant: 'skeleton',
                noRowsVariant: 'skeleton',
                },
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                display: "block",
                overflow: "hidden",
                whiteSpace: "normal",
                lineHeight: 1.4,
              },
              "& .MuiDataGrid-actionsCell": {
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
            }}
            />
          </Paper>
          )}
        </Box>
    );
};

export default IndoorBeaconList;